import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import moment from 'moment'
import { COLORS } from '../../constants'

const CHART_FONT_FAMILY =
  "-apple-system, BlinkMacSystemFont, 'avenir next', avenir, 'helvetica neue', helvetica, ubuntu, roboto, noto, 'segoe ui', arial, sans-serif"

const SERIES_IDS = {
  PRESSURE: 'pressure',
  ALERT_THRESHOLD: 'alertThreshold',
  RESTORE_THRESHOLD: 'restoreThreshold',
}

const DEFAULT_VISIBLE_SERIES = {
  [SERIES_IDS.PRESSURE]: true,
  [SERIES_IDS.ALERT_THRESHOLD]: false,
  [SERIES_IDS.RESTORE_THRESHOLD]: false,
}

const toChartValue = value =>
  value != null && !isNaN(parseFloat(value))
    ? Number(parseFloat(value).toFixed(3))
    : null

const PressureMonitorChart = ({
  data,
  units,
  activateThreshold,
  deactivateThreshold,
}) => {
  // kept in state rather than left to Highcharts so the 30s data refresh,
  // which passes new options, doesn't reset what the user toggled
  const [visibleSeries, setVisibleSeries] = useState(DEFAULT_VISIBLE_SERIES)

  const pressurePoints = data.map(y => [
    moment(y.time).valueOf(),
    toChartValue(y.pressure),
  ])

  // thresholds are drawn as flat series (not yAxis plotLines) so they get a
  // legend entry to toggle, and so the y-axis only stretches to fit them
  // while they're shown
  const thresholdSeries = (id, name, value, color) => {
    const startTime = pressurePoints[0][0]
    const endTime = pressurePoints[pressurePoints.length - 1][0]

    return {
      id,
      name,
      type: 'line',
      color,
      dashStyle: 'Dash',
      lineWidth: 2,
      marker: { enabled: false },
      enableMouseTracking: false,
      visible: visibleSeries[id],
      data: [
        [startTime, value],
        [endTime, value],
      ],
    }
  }

  const alertThresholdValue = toChartValue(activateThreshold)
  const restoreThresholdValue = toChartValue(deactivateThreshold)

  const series = [
    {
      id: SERIES_IDS.PRESSURE,
      name: 'Pressure',
      type: 'spline',
      color: COLORS.BLUE,
      visible: visibleSeries[SERIES_IDS.PRESSURE],
      data: pressurePoints,
    },
  ]

  if (pressurePoints.length && alertThresholdValue != null) {
    series.push(
      thresholdSeries(
        SERIES_IDS.ALERT_THRESHOLD,
        `Alert Threshold (${alertThresholdValue} ${units})`,
        alertThresholdValue,
        COLORS.DARK_RED
      )
    )
  }

  if (pressurePoints.length && restoreThresholdValue != null) {
    series.push(
      thresholdSeries(
        SERIES_IDS.RESTORE_THRESHOLD,
        `Restore Threshold (${restoreThresholdValue} ${units})`,
        restoreThresholdValue,
        COLORS.GREEN
      )
    )
  }

  const chartOptions = {
    credits: { enabled: false },
    exporting: { enabled: false },
    chart: {
      zoomType: 'x',
      style: { fontFamily: CHART_FONT_FAMILY },
    },
    title: { text: null },
    plotOptions: {
      series: {
        events: {
          legendItemClick: ({ target: { userOptions } }) => {
            setVisibleSeries(current => ({
              ...current,
              [userOptions.id]: !current[userOptions.id],
            }))
          },
        },
      },
    },
    yAxis: {
      gridLineWidth: 1,
      gridLineDashStyle: 'longdash',
      labels: {
        format: `{value} ${units}`,
        style: { color: COLORS.BLACK_80, fontSize: '0.75rem' },
      },
      title: { text: null },
    },
    xAxis: {
      gridLineWidth: 1,
      gridLineDashStyle: 'longdash',
      type: 'datetime',
      labels: { style: { color: COLORS.BLACK_80, fontSize: '0.75rem' } },
      title: { text: null },
      crosshair: true,
    },
    tooltip: {
      valueDecimals: 3,
      valueSuffix: ` ${units}`,
      headerFormat:
        "<span style='font-size: 0.875rem; margin-bottom: 0.875rem'>{point.key}</span><br />",
      style: { fontSize: '0.875rem', color: COLORS.BLACK_80 },
    },
    legend: {
      verticalAlign: 'top',
      itemStyle: { fontSize: '0.75rem', color: COLORS.BLACK_80 },
      itemHoverStyle: { color: COLORS.MOON_GRAY },
    },
    series,
  }

  return (
    <div className="PressureMonitorChart">
      <div className="mb3">
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      </div>
    </div>
  )
}

PressureMonitorChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  units: PropTypes.string,
  activateThreshold: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  deactivateThreshold: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
}

PressureMonitorChart.defaultProps = {
  units: 'wc',
  activateThreshold: null,
  deactivateThreshold: null,
}

export default PressureMonitorChart
